/**
 * Scene accumulator for the 3D view: the render mesh, plus the same geometry
 * in its highest-order form (SceneSolids) for the export serializers.
 *
 * Geometry is grouped into *surfaces*, each tagged with a `group`: 0–3 means it
 * belongs to one of the four primary walls (left/back/right/front), and the
 * renderer shows or hides that whole wall — and every element on it (alcoves,
 * doors, windows, openings, its corner chamfer) — as a single on/off, based on
 * whether that wall's room-facing normal points out of the screen (the
 * "dollhouse" cutaway). `group === ALWAYS` (-1) is floor / solid objects, always
 * drawn (GPU back-face culled instead).
 *
 * All vertices (fills + outline endpoints) live in one interleaved buffer
 * [x,y,z, nx,ny,nz, r,g,b]; surfaces hold index ranges into it. Fills, glass
 * (translucent), and outlines are kept in separate index lists so the renderer
 * can draw them in the right passes.
 */

import { type Point } from '@kaiizen/kai-floor-plan';
import { PALETTE, type RGB } from '../editor-model';
import { cross, dot3d, normalize, sub, type Vec3 } from '../math3d';

export type { RGB };

export type SceneSolidLayer = 'floor' | 'ceiling' | 'wall' | 'door' | 'glass' | 'counter' | 'object';

/**
 * A group of solids that belong together in exports with assembly structure
 * (STEP folders): all of one door's panels share one folder instance, so
 * grouping is by object identity — one folder object per edge segment.
 */
export interface SceneSolidFolder {
  name: string;
}

/**
 * One solid the 3D view draws, in its highest-order form: a 2D plan-space
 * vector path extruded vertically over [base, top] (plan units, feet).
 *
 * A closed path is a footprint (rings after the first are holes). An open path
 * is a run of vertical surfaces — `width` feet thick when it's a physical slab
 * standing free of the walls (a door panel), 0 when it's a true plane (glass,
 * niche reveals). The export serializers lower these to planar boundary faces
 * (prismFaces): STL triangulates them, STEP welds them into manifold solids.
 */
export interface SceneSolid {
  layer: SceneSolidLayer;
  /** Display name for exports that name bodies (a named object's own name). */
  name?: string;
  /** The edge segment's folder when this solid is part of a door or window segment. */
  folder?: SceneSolidFolder;
  /** The door this solid is a panel of (an index into the scene's `doors`), so a duplicate door's panels can be dropped together. */
  door?: number;
  rings: Point[][];
  closed: boolean;
  width?: number;
  base: number;
  top: number;
}

/**
 * The box a door occupies: its opening's span along the wall, the wall's
 * thickness across it, and the opening's height — the same box whichever way
 * the door swings, so a doorway drawn from both of its rooms describes the
 * same volume twice. Plan units (feet); `along` is a unit vector.
 */
export interface SceneDoor {
  /** The opening's plan center, halfway through the wall's thickness. */
  center: Point;
  along: Point;
  halfLength: number;
  halfWidth: number;
  base: number;
  top: number;
}

/** A rectangular cutout through a wall leaf: along-span × height span (feet). */
export interface SceneWallCutout {
  s0: number;
  s1: number;
  base: number;
  top: number;
}

/**
 * One straight run of a primary wall's boundary walk: its inner (room-side)
 * line, the mitered outer line, and its door/window/opening cutouts in leaf
 * coordinates (s along the inner line, 0 at innerStart).
 */
export interface SceneWallLeaf {
  innerStart: Point;
  innerEnd: Point;
  outerStart: Point;
  outerEnd: Point;
  /**
   * The leaf's length along its inner line (feet), as the wall walk measured
   * it in the room's own frame — the frame the cutouts' spans are in. Carried
   * rather than remeasured from the points: a leaf re-placed into a level at
   * a fractional offset measures a few ulps off, and a cutout that spans the
   * whole leaf would then stop a hair short of its end.
   */
  length: number;
  /** How far the slab runs past the inner start / end (ft): the square ends' reach. */
  startExtension: number;
  endExtension: number;
  cutouts: SceneWallCutout[];
}

/**
 * A primary wall in its highest-order form: its contiguous boundary leaves
 * (alcove jogs and corner chamfers included), each a square-ended slab
 * extruded over [base, top] with cutouts to punch through as real holes.
 * Export serializers lower every leaf to its own body; neighbors overlap
 * inside the corners.
 */
export interface SceneWall {
  /** Display name from the wall's edge ("back wall", "left wall", …). */
  name: string;
  leaves: SceneWallLeaf[];
  base: number;
  top: number;
}

/** Surface group for always-visible geometry (floor, counters, walls). */
export const ALWAYS = -1;

export interface Surface {
  /** 0–3 = primary wall index (cutaway-culled together), or ALWAYS (-1). */
  group: number;
  /**
   * Drawn only while this group is culled: the outline of a wall's square end
   * where it runs into its neighbor — inside the neighbor while that shows,
   * a real edge once the cutaway hides it. ALWAYS never culls, so an ALWAYS
   * value hides the surface for good.
   */
  unlessGroup?: number;
  /** The door this surface draws (an index into the scene's `doors`); the assembler drops a duplicate door's surfaces when composing. */
  door?: number;
  fillTris: number[];
  glassTris: number[];
  lines: number[];
}

/** Whether a surface draws this frame, given which primary-wall groups face the camera. */
export function surfaceShows(surface: Surface, visibleGroups: readonly boolean[]): boolean {
  if (surface.group >= 0 && !visibleGroups[surface.group]) return false;
  if (surface.unlessGroup === undefined) return true;
  return surface.unlessGroup !== ALWAYS && !visibleGroups[surface.unlessGroup];
}

export class MeshBuilder {
  readonly verts: number[] = []; // interleaved, 9 floats per vertex
  readonly surfaces: Surface[] = [];
  readonly solids: SceneSolid[] = [];
  readonly walls: SceneWall[] = [];
  readonly doors: SceneDoor[] = [];
  private cur: Surface | null = null;

  /** Record the high-order form of geometry being drawn (for export serializers). */
  solid(solid: SceneSolid): void {
    this.solids.push(solid);
  }

  /** Record a primary wall (one continuous export solid with cutouts). */
  wall(wall: SceneWall): void {
    this.walls.push(wall);
  }

  /** Record a door's box; the index tags the door's surfaces and panel solids. */
  door(door: SceneDoor): number {
    this.doors.push(door);
    return this.doors.length - 1;
  }

  /**
   * Start a new surface assigned to a primary-wall group (0–3) or ALWAYS;
   * `unlessGroup` hides it while that group shows; `door` marks it as one
   * door's geometry (see `door()`).
   */
  begin(group: number, unlessGroup?: number, door?: number): void {
    this.cur = { group, fillTris: [], glassTris: [], lines: [] };
    if (unlessGroup !== undefined) this.cur.unlessGroup = unlessGroup;
    if (door !== undefined) this.cur.door = door;
    this.surfaces.push(this.cur);
  }

  end(): void {
    this.cur = null;
  }

  private pushVert(p: Vec3, n: Vec3, c: RGB): number {
    const i = this.verts.length / 9;
    this.verts.push(p[0], p[1], p[2], n[0], n[1], n[2], c[0], c[1], c[2]);
    return i;
  }

  /**
   * A flat quad p0→p1→p2→p3. The stored shading normal is the quad's true
   * geometric normal, oriented to agree with `hint` (which also sets the winding
   * for back-face culling). Pass a zero `hint` to accept the geometric normal as-is.
   */
  quad(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, hint: Vec3, color: RGB): void {
    const s = this.cur!;
    let n = normalize(cross(sub(p1, p0), sub(p3, p0)));
    let order = [p0, p1, p2, p3];
    if (dot3d(hint, hint) > 1e-9 && dot3d(n, hint) < 0) {
      order = [p0, p3, p2, p1];
      n = [-n[0], -n[1], -n[2]];
    }
    const i0 = this.pushVert(order[0], n, color);
    const i1 = this.pushVert(order[1], n, color);
    const i2 = this.pushVert(order[2], n, color);
    const i3 = this.pushVert(order[3], n, color);
    s.fillTris.push(i0, i1, i2, i0, i2, i3);
  }

  /** A translucent quad (glass). Drawn double-sided in the glass pass. */
  glassQuad(p0: Vec3, p1: Vec3, p2: Vec3, p3: Vec3, normal: Vec3, color: RGB): void {
    const s = this.cur!;
    const i0 = this.pushVert(p0, normal, color);
    const i1 = this.pushVert(p1, normal, color);
    const i2 = this.pushVert(p2, normal, color);
    const i3 = this.pushVert(p3, normal, color);
    s.glassTris.push(i0, i1, i2, i0, i2, i3);
  }

  /**
   * A filled triangle fan over a ring of points (for floor polygons, box caps),
   * wound so its geometric normal aligns with `normal` — otherwise the GPU
   * back-face cull would drop polygons whose source winding happens to be flipped.
   */
  fan(pts: Vec3[], normal: Vec3, color: RGB): void {
    const s = this.cur!;
    if (pts.length < 3) return;
    const geo = cross(sub(pts[1], pts[0]), sub(pts[2], pts[0]));
    if (dot3d(geo, normal) < 0) pts = [...pts].reverse();
    const idx = pts.map(p => this.pushVert(p, normal, color));
    for (let i = 1; i < idx.length - 1; i++) s.fillTris.push(idx[0], idx[i], idx[i + 1]);
  }

  /** A dark outline segment. */
  line(a: Vec3, b: Vec3, color: RGB = PALETTE.outline): void {
    const s = this.cur!;
    const ia = this.pushVert(a, [0, 1, 0], color);
    const ib = this.pushVert(b, [0, 1, 0], color);
    s.lines.push(ia, ib);
  }

  /** Outline a ring of points (optionally closed). */
  polyline(pts: Vec3[], close: boolean, color: RGB = PALETTE.outline): void {
    for (let i = 0; i < pts.length - 1; i++) this.line(pts[i], pts[i + 1], color);
    if (close && pts.length > 2) this.line(pts[pts.length - 1], pts[0], color);
  }

  /**
   * Append another builder's RENDER geometry (fills, glass, outlines) with
   * every position and normal passed through `transform`, and every surface's
   * cull group through `mapGroup`. This is how the assembler composes cached
   * per-room meshes into one scene: build each room once, re-place it by
   * transform. `transform.color`, when given, recolors each vertex from its
   * color and its transformed position and normal. Surfaces `keep` refuses
   * are left out (a duplicate door's). The export records (`solids`, `walls`,
   * `doors`) are not carried, since a composed scene is for viewing, not
   * serializing.
   */
  appendTransformed(
    source: MeshBuilder,
    transform: { point: (p: Vec3) => Vec3; normal: (n: Vec3) => Vec3; color?: (color: RGB, point: Vec3, normal: Vec3) => RGB },
    mapGroup: (group: number) => number,
    keep: (surface: Surface) => boolean = () => true,
  ): void {
    const base = this.verts.length / 9;
    const v = source.verts;
    for (let i = 0; i < v.length; i += 9) {
      const p = transform.point([v[i], v[i + 1], v[i + 2]]);
      const n = transform.normal([v[i + 3], v[i + 4], v[i + 5]]);
      const color: RGB = transform.color ? transform.color([v[i + 6], v[i + 7], v[i + 8]], p, n) : [v[i + 6], v[i + 7], v[i + 8]];
      this.verts.push(p[0], p[1], p[2], n[0], n[1], n[2], color[0], color[1], color[2]);
    }
    const shift = (indices: number[]): number[] => indices.map(index => index + base);
    for (const surface of source.surfaces) {
      if (!keep(surface)) continue;
      this.surfaces.push({
        group: mapGroup(surface.group),
        ...(surface.unlessGroup !== undefined && { unlessGroup: mapGroup(surface.unlessGroup) }),
        fillTris: shift(surface.fillTris),
        glassTris: shift(surface.glassTris),
        lines: shift(surface.lines),
      });
    }
  }
}
